<script lang="ts">
  import { onMount } from "svelte"
  import { createEventDispatcher } from "svelte"
  import { t } from "$lib/utils/i18n"
  import userStore, { userStoreActions } from "$lib/stores/userStore"
  import { statsStoreActions } from "$lib/stores/statsStore"
  import { getParentUrl } from "../utils"
  import { encryptJson, generateHmac } from "$lib/utils/encryption"
  import { PUBLIC_HASH, PUBLIC_HMAC_SECRET_KEY } from "$env/static/public"
  import { Button } from "$lib/components/ui/button"
  import { Label } from "$lib/components/ui/label"

  interface Props {
    action: any
    competition: any
    isSubmitting: any
    openTermsDialog: any
    isExample?: boolean
    infoCollection: any
    fraudSettings: any
    embed: boolean
  }

  let {
    action,
    competition,
    isSubmitting = $bindable(),
    openTermsDialog,
    isExample = false,
    infoCollection,
    fraudSettings,
    embed,
  }: Props = $props()

  const dispatch = createEventDispatcher()

  let termsAccepted = $state(false)
  let errorMessage = $state<string | null>(null)
  let selectedTeam = $state("")
  let showTeamError = $state(false)
  let showTeamStep = $state(false)
  let socialUserData = $state<{ name: string; email: string } | null>(null)
  let ThumbmarkJS: any

  let fingerprint = null
  let fingerprintData = null

  // Define available social providers with their configurations
  const availableSocialProviders = {
    google: {
      id: "google",
      label: "Continue with Google",
      icon: `<svg class="w-5 h-5" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
        <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
        <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
        <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
        <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
      </svg>`,
    },
    facebook: {
      id: "facebook",
      label: "Continue with Facebook",
      icon: `<svg class="w-5 h-5" fill="#1877F2" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
        <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
      </svg>`,
    },
    github: {
      id: "github",
      label: "Continue with GitHub",
      icon: `<svg class="w-5 h-5" fill="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
        <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z"/>
      </svg>`,
    },
    discord: {
      id: "discord",
      label: "Continue with Discord",
      icon: `<svg class="w-5 h-5" fill="#5865F2" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
        <path d="M20.317 4.3698a19.7913 19.7913 0 00-4.8851-1.5152.0741.0741 0 00-.0785.0371c-.211.3753-.4447.8648-.6083 1.2495-1.8447-.2762-3.68-.2762-5.4868 0-.1636-.3933-.4058-.8742-.6177-1.2495a.077.077 0 00-.0785-.037 19.7363 19.7363 0 00-4.8852 1.515.0699.0699 0 00-.0321.0277C.5334 9.0458-.319 13.5799.0992 18.0578a.0824.0824 0 00.0312.0561c2.0528 1.5076 4.0413 2.4228 5.9929 3.0294a.0777.0777 0 00.0842-.0276c.4616-.6304.8731-1.2952 1.226-1.9942a.076.076 0 00-.0416-.1057c-.6528-.2476-1.2743-.5495-1.8722-.8923a.077.077 0 01-.0076-.1277c.1258-.0943.2517-.1923.3718-.2914a.0743.0743 0 01.0776-.0105c3.9278 1.7933 8.18 1.7933 12.0614 0a.0739.0739 0 01.0785.0095c.1202.099.246.1981.3728.2924a.077.077 0 01-.0066.1276 12.2986 12.2986 0 01-1.873.8914.0766.0766 0 00-.0407.1067c.3604.698.7719 1.3628 1.225 1.9932a.076.076 0 00.0842.0286c1.961-.6067 3.9495-1.5219 6.0023-3.0294a.077.077 0 00.0313-.0552c.5004-5.177-.8382-9.6739-3.5485-13.6604a.061.061 0 00-.0312-.0286zM8.02 15.3312c-1.1825 0-2.1569-1.0857-2.1569-2.419 0-1.3332.9555-2.4189 2.157-2.4189 1.2108 0 2.1757 1.0952 2.1568 2.419-.0189 1.3332-.9555 2.4189-2.1569 2.4189zm7.9748 0c-1.1825 0-2.1569-1.0857-2.1569-2.419 0-1.3332.9554-2.4189 2.1569-2.4189 1.2108 0 2.1757 1.0952 2.1568 2.419 0 1.3332-.9555 2.4189-2.1568 2.4189Z"/>
      </svg>`,
    },
    // x: {
    //   id: "x",
    //   label: "Continue with X",
    //   icon: `<svg class="w-5 h-5" fill="#000000" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    //     <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
    //   </svg>`,
    // },
    linkedin: {
      id: "linkedin",
      label: "Continue with LinkedIn",
      icon: `<svg class="w-5 h-5" fill="#0077B5" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
        <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
      </svg>`,
    },
    // spotify: {
    //   id: "spotify",
    //   label: "Continue with Spotify",
    //   icon: `<svg class="w-5 h-5" fill="#1DB954" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    //     <path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.521 17.34c-.24.359-.66.48-1.021.24-2.82-1.74-6.36-2.101-10.561-1.141-.418.122-.779-.179-.899-.539-.12-.421.18-.78.54-.9 4.56-1.021 8.52-.6 11.64 1.32.42.18.479.659.301 1.02zm1.44-3.3c-.301.42-.841.6-1.262.3-3.239-1.98-8.159-2.58-11.939-1.38-.479.12-1.02-.12-1.14-.6-.12-.48.12-1.021.6-1.141C9.6 9.9 15 10.561 18.72 12.84c.361.181.54.78.241 1.2zm.12-3.36C15.24 8.4 8.82 8.16 5.16 9.301c-.6.179-1.2-.181-1.38-.721-.18-.601.18-1.2.72-1.381 4.26-1.26 11.28-1.02 15.721 1.621.539.3.719 1.02.419 1.56-.299.421-1.02.599-1.559.3z"/>
    //   </svg>`,
    // },
  }

  // Get enabled social providers based on admin configuration
  const socialProviders = $derived(
    infoCollection?.socialLoginProviders
      ?.map(
        (providerId: string) =>
          availableSocialProviders[
            providerId as keyof typeof availableSocialProviders
          ],
      )
      .filter(Boolean) || [],
  )

  onMount(async () => {
    try {
      await import(
        "https://cdn.jsdelivr.net/npm/@thumbmarkjs/thumbmarkjs/dist/thumbmark.umd.js"
      )
      ThumbmarkJS = (window as any).ThumbmarkJS
      fingerprint = ThumbmarkJS ? await ThumbmarkJS.getFingerprint() : null
      fingerprintData = ThumbmarkJS
        ? await ThumbmarkJS.getFingerprintData()
        : null
    } catch (error) {
      console.error("Error loading ThumbmarkJS:", error)
    }
  })

  async function logUserIn(name: string, email: string, team: string = "") {
    if (embed) {
      window.parent.postMessage(
        {
          type: "PROMOAMP_USER_LOGGED_IN",
          competition: {
            title: competition.title,
            slug: competition.slug,
          },
          user: {
            name,
            email,
          },
        },
        "*",
      )
    } else {
      // Fire off FB Pixel conversion
      // Fire off FB Pixel conversion
      if (window?.fbq && typeof window.fbq === "function") {
        window.fbq("trackCustom", "PromoAmp_SignUp")
      }
    }

    let referredBy = new URLSearchParams(window.location.search).get("ref")

    if (embed) {
      try {
        const parentUrl = await getParentUrl()
        const url = new URL(parentUrl)
        referredBy = url.searchParams.get("ref")
      } catch (error) {
        console.error("Error getting parent URL params:", error)
      }
    }

    console.log("fingerprintData", fingerprintData)

    const { encrypted, iv } = await encryptJson(fingerprintData, PUBLIC_HASH)
    // If logging in with socials, we only have full name
    const userName = name
    const { encrypted: requestId } = await encryptJson(
      {
        userName,
        userEmail: email,
        competitionId: competition.id,
        timestamp: new Date().toISOString(),
      },
      PUBLIC_HASH,
    )

    try {
      const requestBody = JSON.stringify({
        isLogin: true,
        userName,
        userEmail: email,
        userTeam: team || null,
        competitionId: competition.id,
        referredBy,
        fingerprint,
        encryption: encrypted,
        requestId,
        turnstileToken: null,
      })

      // Generate HMAC signature if public key is available
      const headers: Record<string, string> = {
        "Content-Type": "application/json",
      }

      if (PUBLIC_HMAC_SECRET_KEY) {
        const hmacSignature = await generateHmac(
          requestBody,
          PUBLIC_HMAC_SECRET_KEY,
        )
        headers["x-hmac-signature"] = hmacSignature
      }

      const response = await fetch("/api/competitions/create-or-update-entry", {
        method: "POST",
        headers,
        body: requestBody,
      })

      if (!response.ok) {
        const data = await response.json()
        if (data.error) {
          errorMessage = data.error
        }
        throw new Error("Failed to log in")
      }

      const data = await response.json()
      const entryId = data.entryId

      // Fetch updated entry data from API endpoint
      const entryResponse = await fetch(`/api/user/get-entries`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          slug: competition.slug,
          userId: email,
        }),
      })
      if (!entryResponse.ok) throw new Error("Failed to fetch entry data")

      const entryData = await entryResponse.json()
      userStoreActions.setUser({
        name: userName,
        email,
        userTeam: team || null,
        entryId,
        referredBy,
        completedActionIds: entryData.completedActionIds,
        completedActions: entryData.completedActions,
      })

      statsStoreActions.setStats({
        yourEntries: entryData.totalEntries,
        referralCount: entryData.referralCount,
        referrals: entryData.referrals,
      })

      if ($userStore?.user?.completedActionIds?.includes(action.id)) {
        userStoreActions.setOpenActionId(null)

        dispatch("close")
      } else {
        dispatch("close")
      }
    } catch (error) {
      console.error("Error during login:", error)
    }
  }

  function handleSocialLogin(provider: string) {
    if (isExample) return

    isSubmitting = true
    errorMessage = null

    console.log(`Attempting ${provider} login...`)

    const rootUrl = window.location.origin
    // const rootUrl = "https://www.promoamp.com" // Use the production URL for social login
    const redirectUri = `${rootUrl}/api/auth/${provider}/callback`

    let authUrl = ""

    switch (provider) {
      case "facebook":
        authUrl = `https://www.facebook.com/v18.0/dialog/oauth?client_id=1366644637710661&redirect_uri=${encodeURIComponent(redirectUri)}&scope=email&response_type=code`
        break
      case "google":
        authUrl = `https://accounts.google.com/o/oauth2/v2/auth?client_id=464518982852-dqsarc36qqmrahmvhooks553nouu1h19.apps.googleusercontent.com&redirect_uri=${encodeURIComponent(redirectUri)}&response_type=code&scope=openid email profile`
        break
      case "github":
        authUrl = `https://github.com/login/oauth/authorize?client_id=Ov23liTS6JKwTvp4knbL&redirect_uri=${encodeURIComponent(redirectUri)}&scope=user:email`
        break
      case "discord":
        authUrl = `https://discord.com/api/oauth2/authorize?client_id=1392485864755695626&redirect_uri=${encodeURIComponent(redirectUri)}&response_type=code&scope=identify+email`
        break
      case "x": {
        // Generate a proper PKCE code challenge
        const codeVerifier =
          "challenge123456789012345678901234567890123456789012345678901234567890" // Should be random in production
        authUrl = `https://twitter.com/i/oauth2/authorize?response_type=code&client_id=Yzkyb3A3ZTVfZzV6d0RNVk9Cc246MTpjaQ&redirect_uri=${encodeURIComponent(redirectUri)}&scope=tweet.read users.read&state=state&code_challenge=${codeVerifier}&code_challenge_method=plain`
        // Store the code verifier for later use (in production, use localStorage or sessionStorage)
        sessionStorage.setItem("x_code_verifier", codeVerifier)
        break
      }
      case "linkedin":
        authUrl = `https://www.linkedin.com/oauth/v2/authorization?response_type=code&client_id=86cnpopk3bb6yu&redirect_uri=${encodeURIComponent(redirectUri)}&scope=openid profile email`
        break
      case "spotify":
        authUrl = `https://accounts.spotify.com/authorize?response_type=code&client_id=6917a5c36fc74e949c6fd013d632e5f4&redirect_uri=${encodeURIComponent(redirectUri)}&scope=user-read-email user-read-private`
        break
      case "instagram":
        authUrl = `https://api.instagram.com/oauth/authorize?client_id=2052971608485899&redirect_uri=${encodeURIComponent(redirectUri)}&scope=user_profile,user_media&response_type=code`
        break
      default:
        errorMessage = `${provider} login not supported`
        isSubmitting = false
        return
    }

    const popup = window.open(
      authUrl,
      `${provider}-login`,
      "width=600,height=600",
    )

    const messageHandler = (event: MessageEvent) => {
      console.log("Received message from popup:", event.data)

      if (event.data.success && event.data.user) {
        console.log("User name:", event.data.user.name)
        console.log("User email:", event.data.user.email)
        console.log(
          "User profile picture:",
          event.data.user.picture || event.data.user.avatar_url,
        )

        const { name, email } = event.data.user

        // Check if team collection is required
        if (infoCollection?.collectTeam) {
          socialUserData = { name, email }
          showTeamStep = true
          isSubmitting = false
        } else {
          logUserIn(name, email)
          isSubmitting = false
        }

        window.removeEventListener("message", messageHandler)
      } else if (event.data.error) {
        errorMessage = event.data.error
        isSubmitting = false
        window.removeEventListener("message", messageHandler)
      }
    }

    window.addEventListener("message", messageHandler)

    // Also listen for localStorage events (fallback for direct navigation)
    const storageHandler = (event: StorageEvent) => {
      if (event.key === "facebook_auth_result" && event.newValue) {
        try {
          const result = JSON.parse(event.newValue)
          // Only process recent results (within last 30 seconds)
          if (Date.now() - result.timestamp < 30000) {
            if (result.success && result.user) {
              const { name, email } = result.user

              // Check if team collection is required
              if (infoCollection?.collectTeam) {
                socialUserData = { name, email }
                showTeamStep = true
                isSubmitting = false
              } else {
                logUserIn(name, email)
                isSubmitting = false
              }
              cleanup()
            } else if (result.error) {
              errorMessage = result.error
              isSubmitting = false
              cleanup()
            }
            // Clear the localStorage item after processing
            localStorage.removeItem("facebook_auth_result")
          }
        } catch (error) {
          console.error("Error parsing auth result:", error)
        }
      }
    }

    window.addEventListener("storage", storageHandler)

    const cleanup = () => {
      clearInterval(checkClosed)
      window.removeEventListener("message", messageHandler)
      window.removeEventListener("storage", storageHandler)
    }

    // Check if popup was closed manually
    const checkClosed = setInterval(() => {
      if (popup?.closed) {
        cleanup()
        isSubmitting = false
      }
    }, 1000)
  }

  function handleTeamSubmit() {
    // Validate team selection if required
    if (infoCollection?.teamRequired && !selectedTeam) {
      showTeamError = true
      return
    }

    if (socialUserData) {
      isSubmitting = true
      logUserIn(socialUserData.name, socialUserData.email, selectedTeam)
      showTeamStep = false
    }
  }

  function handleBackToSocial() {
    showTeamStep = false
    socialUserData = null
    selectedTeam = ""
    showTeamError = false
  }
</script>

<div class="space-y-2 mb-4 p-4 pb-0">
  {#if !showTeamStep}
    <!-- Social Login Buttons -->
    <div class="space-y-3">
      {#each socialProviders as provider}
        <Button
          variant="outline"
          class="w-full flex items-center justify-center space-x-2 h-11"
          onclick={() => handleSocialLogin(provider.id)}
          disabled={isSubmitting}
        >
          {@html provider.icon}
          <span>{provider.label}</span>
        </Button>
      {/each}
    </div>

    {#if errorMessage}
      <p class="text-red-600 text-sm mt-1">{@html errorMessage}</p>
    {/if}
  {:else}
    <!-- Team Selection Step -->
    <div class="space-y-4">
      <div class="text-center mb-4">
        <h3 class="text-lg font-medium text-gray-900 mb-2">
          Select Your {infoCollection.teamLabel || "Team"}
        </h3>
        <p class="text-sm text-gray-600">
          Signed in as <strong>{socialUserData?.name}</strong>
        </p>
      </div>

      <div class="flex items-center">
        <Label for="team" class="w-1/3">
          {infoCollection.teamLabel || t("widget_login_team")}
          {infoCollection.teamRequired ? "" : t("widget_optional")}
        </Label>
        <div class="w-2/3">
          <select
            name="team"
            class="w-full text-sm border border-input rounded-md px-3 py-2 bg-background"
            bind:value={selectedTeam}
            onfocus={() => (showTeamError = false)}
            required={infoCollection.teamRequired}
          >
            <option value=""
              >Select a {infoCollection.teamLabel?.toLowerCase() ||
                "team"}</option
            >
            {#each infoCollection.teamOptions || [] as teamOption}
              <option value={teamOption}>{teamOption}</option>
            {/each}
          </select>
          {#if showTeamError}
            <p class="text-red-600 text-sm mt-1">
              {t("widget_login_team_required")}
            </p>
          {/if}
        </div>
      </div>

      <div class="flex space-x-2">
        <Button
          variant="outline"
          class="flex-1"
          onclick={handleBackToSocial}
          disabled={isSubmitting}
        >
          Back
        </Button>
        <Button
          class="flex-1"
          onclick={handleTeamSubmit}
          disabled={isSubmitting}
        >
          Continue
        </Button>
      </div>
    </div>
  {/if}

  <!-- <div class="flex items-center !mt-4 !my-2">
    <Checkbox id="terms" class="mr-2" bind:checked={termsAccepted} />
    <Label for="terms" class="text-sm">
      {t("widget_login_terms_intro")}
      <button onclick={openTermsDialog} class="underline cursor-pointer inline">
        {t("widget_login_terms_link")}
      </button>
    </Label>
  </div> -->

  {#if isSubmitting}
    <div class="text-center py-4">
      <div
        class="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-gray-900"
      ></div>
      <p class="text-sm text-gray-600 mt-2">Signing you in...</p>
    </div>
  {/if}
</div>
